<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <b-dropdown id="unduh-dropdown" right variant="primary" menu-class="dropdown-menu-end">
              <template v-slot:button-content>
                <i class="mc-fa icon-outline_download"></i>{{ $t('global.action.download') }}
              </template>
              <b-dropdown-item href="#" @click="downloadReport('xlsx')">
                <img :src="excelIcon" alt="excel" />
                {{ $t('global.action.downloadExcel') }}
              </b-dropdown-item>
              <b-dropdown-item href="#" @click="downloadReport('pdf')">
                <img :src="pdfIcon" alt="pdf" />
                {{ $t('global.action.downloadPdf') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <div :key="summaryKey" class="mc-form mc-report__route-geolocation" style="margin-top: 70px">
      <b-card-title>{{ $t('report.fields.dataSummary') }}</b-card-title>
      <b-row>
        <b-col md="6">
          <GeolocationrouteValue :tripsBetweenGeofences="aggregate" v-if="render" />
        </b-col>
        <b-col md="6">
          <GeolocationrouteGraph
            :tripsBetweenGeofences="tripsBetweenGeofences"
            :average="aggregate.avg"
            :periode="periode"
            v-if="render"
          />
        </b-col>
      </b-row>
    </div>
    <McGrapqlTable
      :key="tableKey"
      bordered
      withCard
      searchable
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="false"
      reportKey="geofences-trip"
      v-if="Object.keys(filters).length > 0"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #cell(startTime)="data"> {{ momentDmMmYH(data.value, true) }} </template>
      <template #cell(stopTime)="data"> {{ momentDmMmYH(data.value, true) }} </template>

      <template #cell(geofenceStart)="data">
        {{ startGeofence }}
      </template>
      <template #cell(geofenceStop)="data">
        {{ stopGeofence }}
      </template>
      <template #cell(geofence)="data">
        <HuggedValue
          :id="`geofence-${data.index}`"
          :value="
            data.item.intersectGeofences
              ? data.item.intersectGeofences.map((item) => item.fenceName).join(', ')
              : null
          "
        />
      </template>

      <template #cell(vehicle)="data">
        {{ data.value.licensePlate }}
      </template>
      <template #cell(driver)="data">
        {{ data.value ? data.value.fullname : '-' }}
      </template>
      <template #cell(tripMileage)="data">
        <span>
          {{ data.value ? parseFloat(data.value / 1000).toFixed(2) : '-' }}
        </span>
        <br />
        <McBadge
          type="button"
          variant="danger"
          :text="$t('report.object.anomaly')"
          v-if="data.item.anomaly.mileage"
        />
      </template>

      <template #cell(tripFuel)="data">
        <span>
          {{ parseFloat(data.value).toFixed(2) ?? '-' }}
        </span>
        <br />
        <McBadge
          type="button"
          variant="danger"
          :text="$t('report.object.anomaly')"
          v-if="data.item.anomaly.tripFuel"
        />
      </template>

      <template #cell(fuelEfficiency)="data">
        <span>
          {{ parseFloat(data.value).toFixed(2) ?? '-' }}
        </span>
        <br />
        <McBadge
          type="button"
          variant="danger"
          :text="$t('report.object.anomaly')"
          v-if="data.item.anomaly.fuelEfficiency"
        />
      </template>

      <template #cell(moveDuration)="data">
        <span>
          {{ data.value ? secondsToHours(data.value) : '-' }}
        </span>
        <br />
        <McBadge
          type="button"
          variant="danger"
          :text="$t('report.object.anomaly')"
          v-if="data.item.anomaly.moveDuration"
        />
      </template>
      <template #cell(idleDuration)="data">
        <span>
          {{ data.value ? secondsToHours(data.value) : '-' }}
        </span>
        <br />
        <McBadge
          type="button"
          variant="danger"
          :text="$t('report.object.anomaly')"
          v-if="data.item.anomaly.idleDuration"
        />
      </template>
      <template #cell(stopDuration)="data">
        <span>
          {{ data.value ? secondsToHours(data.value) : '-' }}
        </span>
        <br />
        <McBadge
          type="button"
          variant="danger"
          :text="$t('report.object.anomaly')"
          v-if="data.item.anomaly.stopDuration"
        />
      </template>

      <template #cell(tripDuration)="data">
        <span>
          {{ data.value ? secondsToHours(data.value) : '-' }}
        </span>
        <br />
        <McBadge
          type="button"
          variant="danger"
          :text="$t('report.object.anomaly')"
          v-if="data.item.anomaly.tripDuration"
        />
      </template>
      <template #cell(speed)="data">
        <span>
          {{ parseFloat(data.value).toFixed(2) }}
        </span>
        <br />
        <McBadge
          type="button"
          variant="danger"
          :text="$t('report.object.anomaly')"
          v-if="data.item.anomaly.speed"
        />
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
  </Layout>
</template>
<script>
import McButton from '@/components/McButton'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import fields from './lib/fields/fields'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import reportList from '../../lib/reportList.js'
import {
  EXPORT_TRIPS_BETWEEN_GEOFENCES_REPORT,
  GET_TRIPS_BETWEEN_GEOFENCES,
} from '@/services/api/report/report'
import McBadge from '@/components/McBadge'
import { MixPanel } from '@/mixin/mixpanel'
import GeolocationrouteGraph from '../geolocationroute/Graph.vue'
import GeolocationrouteValue from '../geolocationroute/Value.vue'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import { mapGetters } from 'vuex'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import HuggedValue from '../../components/HuggedValue.vue'
moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McReportDownloader,
    McButton,
    ModalFilter,
    McBadge,
    GeolocationrouteGraph,
    GeolocationrouteValue,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
    HuggedValue,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      tableKey: 0,
      isBusy: false,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      aggregate: null,
      startGeofence: null,
      stopGeofence: null,
      summary: {},
      anomaly: {},
      reportKey: 'geofences-trip',
      filterDetail: {},
      summaryKey: 50,
      tripsBetweenGeofences: [],
      render: false,
      queryCache: new QueryCache(),
      message: '',
      isError: false,
      modalVerifReport: 100,
    }
  },
  mounted() {
    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    summaryFields() {
      return [
        {
          key: 'mileage',
          label: this.$t('report.fields.averageMileage'),
          convert: (value) => (value ? parseFloat((value / 1000).toFixed(2)).toFixed(2) : '0'),
          unit: 'Km',
        },
        {
          key: 'tripFuel',
          label: this.$t('report.fields.averageTripFuel'),
          convert: (value) => (value ? parseFloat(value).toFixed(2) : '0'),
          unit: 'L',
        },
        {
          key: 'fuelEfficiency',
          label: this.$t('report.fields.averageFuelEfficiency'),
          convert: (value) => (value ? parseFloat(value).toFixed(2) : '0'),
          unit: 'Liter',
        },
        {
          key: 'moveDuration',
          label: this.$t('report.fields.averageMoveDuration'),
          convert: (value) => this.convertToTime(value),
          unit: 'duration',
        },
        {
          key: 'idleDuration',
          label: this.$t('report.fields.averageIdleDuration'),
          convert: (value) => this.convertToTime(value),
          unit: 'duration',
        },
        {
          key: 'stopDuration',
          label: this.$t('report.fields.averageStopDuration'),
          convert: (value) => this.convertToTime(value),
          unit: 'duration',
        },
        {
          key: 'tripDuration',
          label: this.$t('report.fields.averageTripDuration'),
          convert: (value) => this.convertToTime(value),
          unit: 'duration',
        },
        {
          key: 'speed',
          label: this.$t('report.fields.avgSpeed'),
          convert: (value) => parseFloat(value).toFixed(2) ?? '0',
          unit: `Km/${this.$t('global.time.hours')}`,
        },
      ]
    },
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    fields() {
      return this.reportFields
    },
    columns() {
      return this.reportFields
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const filters = {}
        Object.keys(this.filters).forEach((item) => {
          if (
            this.filters[item] !== null &&
            ((Array.isArray(this.filters[item]) && this.filters[item].length > 0) ||
              (!Array.isArray(this.filters[item]) && this.filters[item]))
          ) {
            filters[item] = this.filters[item]
          }
        })
        const { periode, ...otherFilter } = filters
        const startDate =
          periode.length > 0 ? moment(periode[0] + ' 00:00:00.000').toISOString() : null
        const endDate =
          periode.length > 0 ? moment(periode[1] + ' 23:59:59.000').toISOString() : null
        const model = {
          dateRange: {
            start: startDate,
            end: endDate,
          },
          ...otherFilter,
        }
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    apiClient() {
      return {
        get: async (filter) => {
          const { valid, model } = this.generateFilter
          if (!valid) return {}
          const self = this
          self.render = false
          self.isBusy = true
          const res = await this.queryCache.fetch(
            GET_TRIPS_BETWEEN_GEOFENCES,
            {
              filter: {
                ...model,
                ...filter,
              },
            },
            self.reportKey
          )
          const result = res.tripsBetweenGeofences.tripsBetweenGeofences
          self.tripsBetweenGeofences = result.data
          self.totalRows = result?.pagination?.total ?? 0
          self.aggregate = result.aggregate
          self.startGeofence = result.info.startGeofence.fenceName
          self.stopGeofence = result.info.endGeofence.fenceName
          self.summary = result.aggregate.avg
          self.anomaly = result.aggregate.anomaly
          self.isBusy = false
          self.message = res.tripsBetweenGeofences.message
          self.isError = !res.tripsBetweenGeofences.isSuccessful
          self.modalVerifReport += 1
          self.render = true
          return result
        },
      }
    },
    periode() {
      return this.filters?.periode ?? []
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDateShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDateShortMonth(this.filters.periode[1])
        : '-'
    },
    passedGeofenceLabel() {
      if (this.filters.intersectGeofenceIds && this.filters.intersectGeofenceIds.length > 0) {
        return this.filterDetail != {} ? this.filterDetail.geofence : '-'
      } else {
        return '-'
      }
    },
    passedGeofenceCount() {
      return this.filters.intersectGeofenceIds && this.filters.intersectGeofenceIds.length > 0
        ? this.filters.intersectGeofenceIds.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
  },
  methods: {
    handleColumnReport(fields) {
      this.reportFields = fields
      this.tableKey += 1
      this.summaryKey += 1
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.tableKey += 1
      this.summaryKey += 1
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    formattingValue(value, unit) {
      if (!value) return null
      if (unit === 'duration') {
        const duration = value.split(':')
        return `${duration[0]} ${this.$t('global.time.hours')}, ${duration[1]} ${this.$t(
          'global.time.minutes'
        )}, ${duration[2]} ${this.$t('global.time.seconds')}`
      } else {
        return `${value} ${unit}`
      }
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.tripsBetweenGeofencesReport.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension) {
      this.track(`Download ${this.report.event} ${extension == 'pdf' ? '- PDF' : '- Excel'}`)

      const { valid, model } = this.generateFilter
      if (!valid) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters
      let filterVar = {
        ...model,
        column: this.columns,
        subtitle: `Periode : ${this.momentDateLongMonth(periode[0])} - ${this.momentDateLongMonth(
          periode[1]
        )}`,
        downloadAs: extension == 'pdf' ? 'pdf' : 'excel',
      }
      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      this.$apollo
        .query({
          query: EXPORT_TRIPS_BETWEEN_GEOFENCES_REPORT,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          this.showDownloader(periode, extension, res.data.tripsBetweenGeofencesReport)
        })
    },
  },
}
</script>
